import { APP_CONFIG, SERVICE_NAME } from "@/constants/config.constants";
import {
  ALLOWED_ENVIRONMENTS,
  DATADOG_TOKEN,
  ENVIRONMENT_CONSTANTS,
} from "@/constants/environment.constants";
import { getEnv } from "@/helpers/environment/environment.helpers";
import { LOGGER_LEVEL, Logger } from "@aircall/logger";

export type UserSession = {
  company_id: number;
  user_id: string;
  tier_level: string;
  provider: string;
  is_trial: boolean;
};

export type ExtraContext = {
  connect_as: {
    admin_sso_id: string;
    impersonated_user: string;
  };
};

export const logger = new Logger({
  token: DATADOG_TOKEN,
  /**
   * The minimal log level for the logger.
   * If set to `info`, the `debug` logs will be ignored
   * It might be overriden in src/sagas/logger.ts if the enable_verbose_debug_log_mode FF i set to true
   */
  level: LOGGER_LEVEL.info,
  // display logs into the browser console instead of sending them to Datadog
  debug: getEnv() === ENVIRONMENT_CONSTANTS.DEVELOPMENT,
});

/**
 * Initialize the logger with the correct configuration.
 */
export function initLogger(): void {
  if (
    ALLOWED_ENVIRONMENTS.includes(APP_CONFIG.environment) &&
    !logger.initialized
  ) {
    logger.init({
      beforeSend: (log) => {
        log.version = APP_CONFIG.release;
        log.environment = APP_CONFIG.environment;
      },
    });
    logger.setLevel(LOGGER_LEVEL.info);
    logger.setContext({
      service: SERVICE_NAME,
      user_session: {
        device: SERVICE_NAME,
        environment: APP_CONFIG.environment,
        release: APP_CONFIG.release,
      },
      user: {},
    });
  }
}

/**
 * Set the logger context with the current user session.
 * @param userSession - The user session containing the user id, company id, etc.
 */
export function setLoggerContext(
  userSession: UserSession,
  extraContext?: ExtraContext
): void {
  if (logger.initialized) {
    logger.setContext({
      service: SERVICE_NAME,
      user_session: {
        ...userSession,
        ...APP_CONFIG,
      },
      ...extraContext,
    });
  }
}

/**
 * Remove the user session from the logger context.
 */
export function clearLoggerContext(): void {
  if (logger.initialized) {
    logger.setContext({
      service: SERVICE_NAME,
      user_session: {
        device: SERVICE_NAME,
        environment: process.env.REACT_APP_BUILD_ENV,
        release: APP_CONFIG.release,
      },
    });
  }
}
